import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { useForm } from "react-hook-form";
import styles from "./my-institution.module.css";
import { toast } from "react-toastify";
import CustomButton from "../../../../common/custom-button/custom-button";
import ProfileTitleHr from "../../../../components/profile-title-hr/profile-title-hr";
import { validateDate } from "../../../../utils/format-date/format-date";
import InstitutionInfo from "./components/institution-info";
import { getInstitutionDetails, updateInstitution } from "../../../../services/institution";
import { useDispatch, useSelector } from "react-redux";
import { DATE_FORMAT } from "../../../../constants/common";
import dayjs from "dayjs";
import InstitutionContact from "./components/institution-contact";
import InstituteRegulatoryAcademic from "./components/institute-regulatory-academic";
import InstituteSupportingDoc from "./components/institute-supporting-doc";
import { setUser } from "../../../../redux/features/userSlice";

const PROFILE_FORMS = [
  {
    label: "School Details",
    Component: InstitutionInfo,
  },
  {
    label: "Contact Information",
    Component: InstitutionContact,
  },
  {
    label: "Regulatory & Academic Info",
    Component: InstituteRegulatoryAcademic,
  },
  {
    label: "Supporting Documents",
    Component: InstituteSupportingDoc,
  },
];

const MyInstitution = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.user);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [institutionDetails, setInstitutionDetails] = useState(null);

  const form1 = useForm({
    mode: "onChange",
    defaultValues: {
      institution_name: "",
      prinicipal_first_name: "",
      prinicipal_middle_name: "",
      prinicipal_last_name: "",
      deputy_prinicipal_first_name: "",
      deputy_prinicipal_middle_name: "",
      deputy_prinicipal_last_name: "",
      registration_number: "",
      curriculum: "",
      school_type: "",
      ownership_type: "",
      established_date: "",
      vision: "",
      institution_logo: "",
    },
  });
  const form2 = useForm({
    mode: "onChange",
    defaultValues: {
      physical_address: "",
      lat: "",
      lng: "",
      postal_address: "",
      province: "",
      country: "",
      principal_phone_number: "",
      principal_email_address: "",
      deputy_principal_phone_number: "",
      deputy_principal_email_address: "",
      school_phone_number: "",
      school_email: "",
      school_website_url: "",
    },
  });

  const form3 = useForm({
    mode: "onChange",
    defaultValues: {
      education_authority: "",
      levels_offered: "",
      total_students: "",
      total_teachers: "",
      total_classes: "",
      total_support_staffs: "",
      boarding_or_day: "",
    },
  });

  const form4 = useForm({
    mode: "onChange",
    defaultValues: {
      accrediation_certificate: "",
      operating_license: "",
      additional_document: "",
      logo: "",
      letter_head: "",
    },
  });

  const FORM_SET = [form1, form2, form3, form4];

  useEffect(() => {
    getInstitutionData();
  }, []);

  const getInstitutionData = async () => {
    try {
      setLoading(true);
      const response = await getInstitutionDetails(user?.institution_id);
      if (response?.data) {
        const data = response?.data?.data;
        setInstitutionDetails(data);
        populateFormData(data);
        return data;
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const getImageName = (img) => {
    if (!img) return {};

    const decodedUrl = decodeURIComponent(img);
    const imageName = decodedUrl.split("#").pop();

    return imageName 
      ? { name: imageName, url: img } 
      : {};
  };

  const populateFormData = (data) => {
    form1.reset({
      institution_name: data?.institution_name ?? "",
      prinicipal_first_name: data?.prinicipal_first_name ?? "",
      prinicipal_middle_name: data?.prinicipal_middle_name ?? "",
      prinicipal_last_name: data?.prinicipal_last_name ?? "",
      deputy_prinicipal_first_name: data?.deputy_prinicipal_first_name ?? "",
      deputy_prinicipal_middle_name: data?.deputy_prinicipal_middle_name ?? "",
      deputy_prinicipal_last_name: data?.deputy_prinicipal_last_name ?? "",
      registration_number: data?.registration_number ?? "",
      curriculum: data?.curriculum ?? "",
      school_type: data?.school_type ?? "",
      ownership_type: data?.ownership_type ?? "",
      established_date: data?.established_date
        ? validateDate(data?.established_date)
        : "",
      institution_logo: getImageName(data?.institution_logo),
      vision: data?.vision ?? "",
    });

    form2.reset({
      physical_address: data?.physical_address ?? "",
      postal_address: data?.postal_address ?? "",
      province: data?.province ?? "",
      country: data?.country ?? "Zimbabwe",
      principal_phone_number: data?.principal_phone_number ?? "",
      principal_email_address: data?.principal_email_address ?? "",
      deputy_principal_phone_number: data?.deputy_principal_phone_number ?? "",
      deputy_principal_email_address: data?.deputy_principal_email_address ?? "",
      school_phone_number: data?.school_phone_number ?? "",
      school_email: data?.school_email ?? "",
      school_website_url: data?.school_website_url ?? "",
      lat: data?.latitude ?? "",
      lng: data?.longitude ?? "",
    });

    form3.reset({
      education_authority: data?.education_authority ?? "",
      levels_offered: data?.levels_offered ?? "",
      total_students: data?.total_students ?? "",
      total_teachers: data?.total_teachers ?? "",
      total_classes: data?.total_classes ?? "",
      total_support_staffs: data?.total_support_staffs ?? "",
      boarding_or_day: data?.boarding_or_day ?? "",
    });

    form4.reset({
      accrediation_certificate: getImageName(data?.accrediation_certificate),
      operating_license: getImageName(data?.operating_license),
      additional_document: getImageName(data?.additional_document),
      logo: getImageName(data?.logo),
      letter_head: getImageName(data?.letter_head),
    });
  };

  const handleSubmit = async () => {
    try {
      const form1Values = form1.getValues();
      const form2Values = form2.getValues();
      const form3Values = form3.getValues();
      const form4Values = form4.getValues();

      const formdata = new FormData();
      // Form 1
      formdata.append("institution_name", form1Values.institution_name);
      formdata.append("prinicipal_first_name", form1Values.prinicipal_first_name);
      formdata.append("prinicipal_middle_name", form1Values.prinicipal_middle_name);
      formdata.append("prinicipal_last_name", form1Values.prinicipal_last_name);
      formdata.append("deputy_prinicipal_first_name", form1Values.deputy_prinicipal_first_name);
      formdata.append("deputy_prinicipal_middle_name", form1Values.deputy_prinicipal_middle_name);
      formdata.append("deputy_prinicipal_last_name", form1Values.deputy_prinicipal_last_name);
      formdata.append("registration_number", form1Values.registration_number);
      formdata.append("curriculum", form1Values.curriculum);
      formdata.append("school_type", form1Values.school_type);
      formdata.append("ownership_type", form1Values.ownership_type);
      formdata.append("vision", form1Values.vision);

      if (form1Values.institution_logo?.size) {
        formdata.append("institution_logo", form1Values.institution_logo);
      }

      formdata.append(
        "established_date",
        form1Values.established_date ? dayjs(form1Values.established_date).format(DATE_FORMAT.DD_MM_YYYY) : ""
      );

      // Form 2
      formdata.append("physical_address", form2Values.physical_address);
      formdata.append("postal_address", form2Values.postal_address);
      formdata.append("province", form2Values.province);
      formdata.append("country", form2Values.country);
      formdata.append("principal_phone_number", form2Values.principal_phone_number);
      formdata.append("principal_email_address", form2Values.principal_email_address);
      formdata.append("deputy_principal_phone_number", form2Values.deputy_principal_phone_number);
      formdata.append("deputy_principal_email_address", form2Values.deputy_principal_email_address);
      formdata.append("school_phone_number", form2Values.school_phone_number);
      formdata.append("school_email", form2Values.school_email);
      formdata.append("school_website_url", form2Values.school_website_url);

      formdata.append("longitude", form2Values.lng);
      formdata.append("latitude", form2Values.lat);

      // Form 3
      formdata.append("education_authority", form3Values.education_authority);
      formdata.append("levels_offered", form3Values.levels_offered);
      formdata.append("total_students", form3Values.total_students);
      formdata.append("total_teachers", form3Values.total_teachers);
      formdata.append("total_classes", form3Values.total_classes);
      formdata.append("total_support_staffs", form3Values.total_support_staffs);
      formdata.append("boarding_or_day", form3Values.boarding_or_day);

      // Form 4
      if (form4Values.accrediation_certificate?.size) {
        formdata.append("accrediation_certificate", form4Values.accrediation_certificate);
      }
      if (form4Values.operating_license?.size) {
        formdata.append("operating_license", form4Values.operating_license);
      }
      if (form4Values.additional_document?.size) {
        formdata.append("additional_document", form4Values.additional_document);
      }
      if (form4Values.logo?.size) {
        formdata.append("logo", form4Values.logo);
      }
      if (form4Values.letter_head?.size) {
        formdata.append("letter_head", form4Values.letter_head);
      }

      setLoading(true);
      await updateInstitution(institutionDetails?.id, formdata);
      const updatedInstitution = await getInstitutionData();

      const updatedUser = {
        ...user,
        institution_logo: updatedInstitution?.institution_logo,
        institution: {
          ...user?.institution,
          institution_name: updatedInstitution?.institution_name,
        },
      };
      dispatch(setUser(updatedUser));
      setIsEditMode(false);
      toast.success("Institution/School updated successfully!");
    } catch (err) {
      console.log(err?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInstitute = async () => {
    try {
      const results = await Promise.all([
        form1.trigger(),
        form2.trigger(),
        form3.trigger(),
        form4.trigger(),
      ]);

      const allValid = results.every(Boolean);
      if (allValid) {
        await handleSubmit();
      } else {
        const form2Values = form2.getValues();
        if (form2Values.physical_address && (!form2Values.lat || !form2Values.lng)) {
          toast.error("Please select a valid physical address from the dropdown to set location coordinates.");
          return;
        }
        toast.error("Some fields need your attention. Please review and try again.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    if (institutionDetails) {
      populateFormData(institutionDetails);
    }
  };

  return (
    <Spin spinning={loading}>
      <div>
        <div className={styles["profile-buttons-container"]}>
          <div className={styles["profile-buttons"]}>
            {isEditMode ? (
              <>
                <CustomButton
                  type="text"
                  onClick={handleCancelEdit}
                  title="Cancel"
                  className={styles.cancelButton}
                />
                <CustomButton
                  icon={
                    <img
                      src="/assets/images/edit.svg"
                      style={{ marginTop: 2 }}
                      alt="editIcon"
                    />
                  }
                  iconPosition="start"
                  onClick={handleSaveInstitute}
                  title="Save"
                />
              </>
            ) : (
              <CustomButton
                icon={
                  <img
                    src="/assets/images/edit.svg"
                    style={{ marginTop: 2 }}
                    alt="editIcon"
                  />
                }
                iconPosition="start"
                onClick={() => setIsEditMode(!isEditMode)}
                title="Edit Institute"
              />
            )}
          </div>
        </div>

        <div className={styles["profile-section"]}>
          {PROFILE_FORMS?.map((item, index) => {
            const Component = item?.Component;
            return (
              <div key={index}>
                <ProfileTitleHr label={item?.label} />
                <Component
                  {...FORM_SET[index]}
                  isEditMode={isEditMode}
                />
              </div>
            )
          })}
        </div>
      </div>
    </Spin>
  );
};

export default MyInstitution;
